/**
 * ZONE DELETE DIALOG — Conferma di cancellazione di una zona. Elenca le aziende
 * collegate (resteranno senza zona), così chi cancella sa cosa smuove.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { zoneApi } from '../../entities/zone/zoneApi';
import { extractError } from '../../store/thunkHelpers';
import type { Zone, ZoneBusiness } from '../../entities/zone/zoneTypes';
import { useToast } from '../useToast';

interface Props {
  zone: Zone;
  onClose: () => void;
  /** Chiamata a cancellazione avvenuta: il padre ricarica l'elenco. */
  onDeleted: () => void;
}

export function ZoneDeleteDialog({ zone, onClose, onDeleted }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  // null = elenco aziende ancora in caricamento.
  const [businesses, setBusinesses] = useState<ZoneBusiness[] | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    zoneApi.businesses(zone.zoneId).then(setBusinesses).catch((err) => {
      toast({ severity: 'error', message: extractError(err) });
      onClose();
    });
    // Si carica una volta per zona: toast e onClose del padre non fanno parte della chiave.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zone.zoneId]);

  async function handleDelete() {
    setDeleting(true);
    try {
      await zoneApi.remove(zone.zoneId);
      onDeleted();
    } catch (err) {
      toast({ severity: 'error', message: extractError(err) });
      setDeleting(false);
    }
  }

  const name = zone.name;

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth className="app-dialog">
      <DialogTitle className="tc-head">
        <span className="tc-kicker">{t('settings.zones.deleteTitle')}</span>
      </DialogTitle>
      <DialogContent className="app-dialog__body">
        {businesses === null ? (
          <p className="text-muted">{t('common.loading')}</p>
        ) : businesses.length > 0 ? (
          <>
            <p>{t('settings.zones.deleteWithBusinesses', { name })}</p>
            <ul className="zone-settings__businesses">
              {businesses.map((b) => <li key={b.businessId}>{b.name}</li>)}
            </ul>
          </>
        ) : (
          <p>{t('settings.zones.deleteNoBusinesses', { name })}</p>
        )}
      </DialogContent>
      <DialogActions>
        <Button type="button" variant="outlined" onClick={onClose}>{t('common.cancel')}</Button>
        <button type="button" className="btn-danger" onClick={handleDelete} disabled={businesses === null || deleting}>
          {t('settings.zones.delete')}
        </button>
      </DialogActions>
    </Dialog>
  );
}
